import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyEamesMode({ brand }: ModeRendererProps) {
  // Human-centered rigor; playful precision for product storytelling
  // Pattern tiles, ply-esque layering, soft grids
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Eames-style pattern tiles grid */}
      {Array.from({ length: 8 }).map((_, row) =>
        Array.from({ length: 10 }).map((_, col) => {
          const x = 60 + col * 68;
          const y = 80 + row * 68;
          const pattern = (row * 10 + col) % 6;
          
          // Different tile patterns inspired by Eames
          if (pattern === 0) {
            // Leaf shape
            return (
              <g key={`tile-${row}-${col}`}>
                <ellipse cx={x + 34} cy={y + 34} rx="28" ry="18" fill={brand.aqua} opacity={0.6} />
                <ellipse cx={x + 34} cy={y + 34} rx="18" ry="28" fill={brand.violet} opacity={0.6} />
              </g>
            );
          } else if (pattern === 1) {
            // Dots
            return (
              <g key={`tile-${row}-${col}`}>
                <circle cx={x + 20} cy={y + 20} r="8" fill={brand.amber} opacity={0.7} />
                <circle cx={x + 48} cy={y + 20} r="8" fill={brand.violet} opacity={0.7} />
                <circle cx={x + 20} cy={y + 48} r="8" fill={brand.violet} opacity={0.7} />
                <circle cx={x + 48} cy={y + 48} r="8" fill={brand.aqua} opacity={0.7} />
              </g>
            );
          } else if (pattern === 2) {
            // Cross
            return (
              <g key={`tile-${row}-${col}`}>
                <rect x={x + 28} y={y + 12} width="12" height="44" fill={brand.violet} opacity={0.6} />
                <rect x={x + 12} y={y + 28} width="44" height="12" fill={brand.aqua} opacity={0.6} />
              </g>
            );
          } else if (pattern === 3) {
            // Concentric squares (Vasarely influence)
            return (
              <g key={`tile-${row}-${col}`}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <rect
                    key={`square-${i}`}
                    x={x + 10 + i * 6}
                    y={y + 10 + i * 6}
                    width={48 - i * 12}
                    height={48 - i * 12}
                    fill="none"
                    stroke={i % 2 === 0 ? brand.amber : brand.deep}
                    strokeWidth="2"
                    opacity={0.7}
                  />
                ))}
              </g>
            );
          } else if (pattern === 4) {
            // Diagonal stripes
            return (
              <g key={`tile-${row}-${col}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <line
                    key={`stripe-${i}`}
                    x1={x + i * 8}
                    y1={y}
                    x2={x + 68}
                    y2={y + 68 - i * 8}
                    stroke={i % 2 === 0 ? brand.aqua : brand.violet}
                    strokeWidth="3"
                    opacity={0.5}
                  />
                ))}
              </g>
            );
          } else {
            // Circle with optical rings
            return (
              <g key={`tile-${row}-${col}`}>
                <circle cx={x + 34} cy={y + 34} r="30" fill={brand.amber} opacity={0.4} />
                {Array.from({ length: 5 }).map((_, i) => (
                  <circle
                    key={`ring-${i}`}
                    cx={x + 34}
                    cy={y + 34}
                    r={26 - i * 5}
                    fill="none"
                    stroke={brand.deep}
                    strokeWidth="1"
                    opacity={0.6}
                  />
                ))}
              </g>
            );
          }
        })
      )}
      
      {/* Ply-esque layering effect in middle section */}
      {Array.from({ length: 6 }).map((_, i) => {
        const offset = i * 12;
        const opacity = 0.15 - i * 0.02;
        return (
          <rect
            key={`layer-${i}`}
            x={80 + offset}
            y={620 + offset}
            width={634 - offset * 2}
            height={180 - offset * 2}
            fill="none"
            stroke={brand.violet}
            strokeWidth="2"
            opacity={opacity}
            rx="8"
          />
        );
      })}
      
      {/* Playful organic shapes */}
      <ellipse cx="180" cy="710" rx="60" ry="40" fill={brand.aqua} opacity={0.5} />
      <ellipse cx="320" cy="710" rx="50" ry="35" fill={brand.amber} opacity={0.5} />
      <ellipse cx="460" cy="710" rx="55" ry="38" fill={brand.violet} opacity={0.5} />
      <ellipse cx="600" cy="710" rx="52" ry="42" fill={brand.aqua} opacity={0.5} />
      
      {/* Bottom soft grid with rounded elements */}
      {Array.from({ length: 4 }).map((_, row) =>
        Array.from({ length: 7 }).map((_, col) => {
          const x = 80 + col * 90;
          const y = 860 + row * 60;
          const colors = [brand.aqua, brand.violet, brand.amber];
          
          return (
            <rect
              key={`soft-grid-${row}-${col}`}
              x={x}
              y={y}
              width="70"
              height="45"
              rx="12"
              fill={colors[(row + col) % 3]}
              opacity={0.4}
              stroke={brand.deep}
              strokeWidth="1"
            />
          );
        })
      )}
      
      {/* Optical wave pattern overlay */}
      {Array.from({ length: 30 }).map((_, i) => {
        const x = 60 + i * 23;
        return (
          <line
            key={`wave-${i}`}
            x1={x}
            y1={850 + Math.sin(i * 0.5) * 20}
            x2={x}
            y2={1080 - Math.sin(i * 0.5) * 20}
            stroke={brand.violet}
            strokeWidth="1"
            opacity={0.15}
          />
        );
      })}
    </g>
  );
}
